import { LANGUAGE_CODES } from './schemas/sas-domain';
import { PaymentType } from './schemas/sas-domain/payment';
import { ValueOf } from './utils/types';

export const SAS_USER_SESSION_ID = 'sas-user-session-id';
export const SAS_CORRELATION_ID = 'sas-correlation-id';

export const EUROBONUS_PROGRAM_KEY = 'SK';
export const EUROBONUS_PREFIX_BASIC_TIER = 'EBB';

export type Environment = ValueOf<typeof Environment>;
export const Environment = {
  Development: 'development',
  Test: 'test',
  Production: 'production',
} as const;

export const parseEnvironment = (value: string): Environment | undefined =>
  Object.values(Environment).find(env => env === value);

export interface PaymentTypeInfo {
  FOPTYPE: string;
  SUB_TYPE?: Record<string, string>;
  freeText?: Record<'start' | 'end', number>;
}

export const PAYMENT_TYPE_INFO: Record<PaymentType, PaymentTypeInfo> = {
  CARD: {
    FOPTYPE: 'CC',
    SUB_TYPE: {
      AMEX: 'AX',
      DANKORT: 'DK',
      DINERS: 'DC',
      DISCOVER: 'DS',
      ELECTRON: 'VE',
      JCB: 'JC',
      MAESTRO: 'MA',
      VISA: 'VI',
      UATP: 'TP',
      MC: 'CA',
    },
    freeText: {
      start: 4,
      end: 8,
    },
  },
  VOUCHER: {
    FOPTYPE: 'EBK',
  },
  EUROBONUS: {
    FOPTYPE: 'EBP',
  },
  SWISH: {
    FOPTYPE: 'IBP',
    freeText: {
      start: 4,
      end: 10,
    },
  },
  WALLET: {
    FOPTYPE: 'WC',
  },
  KLARNA: {
    FOPTYPE: 'RC',
  },
  TRAVELACCOUNT: {
    FOPTYPE: 'CC',
    SUB_TYPE: {
      AMEX: 'AX',
      DANKORT: 'DK',
      DINERS: 'DC',
      DISCOVER: 'DS',
      ELECTRON: 'VE',
      JCB: 'JC',
      MAESTRO: 'MA',
      VISA: 'VI',
      UATP: 'TP',
      MC: 'CA',
    },
  },
  INTERNETBANKING: {
    FOPTYPE: '',
  },
};

export const POS = {
  SE: 'SE',
  DK: 'DK',
  NO: 'NO',
  BE: 'BE',
  FI: 'FI',
  CA: 'CA',
  CN: 'CN',
  FR: 'FR',
  DE: 'DE',
  HK: 'HK',
  IE: 'IE',
  IT: 'IT',
  JP: 'JP',
  NL: 'NL',
  PL: 'PL',
  ES: 'ES',
  CH: 'CH',
  GB: 'GB',
  US: 'US',
  LU: 'LU',
  KR: 'KR',
} as const;

export type POS = keyof typeof POS;

export type CurrencyMapItem = {
  CURRENCY_CODE: string;
  PRECISION: number;
  locale: string;
};

export const currencyMap: { [k in keyof typeof POS]: CurrencyMapItem } = {
  [POS.SE]: {
    CURRENCY_CODE: 'SEK',
    PRECISION: 0,
    locale: LANGUAGE_CODES.SV_SE,
  },
  [POS.NO]: {
    CURRENCY_CODE: 'NOK',
    PRECISION: 0,
    locale: LANGUAGE_CODES.NO_NO,
  },
  [POS.DK]: {
    CURRENCY_CODE: 'DKK',
    PRECISION: 0,
    locale: LANGUAGE_CODES.DA_DK,
  },
  [POS.BE]: {
    CURRENCY_CODE: 'EUR',
    PRECISION: 2,
    locale: 'de-BE',
  },
  [POS.FI]: {
    CURRENCY_CODE: 'EUR',
    PRECISION: 2,
    locale: LANGUAGE_CODES.EN_FI,
  },
  [POS.CA]: {
    CURRENCY_CODE: 'CAD',
    PRECISION: 2,
    locale: LANGUAGE_CODES.EN_CA,
  },
  [POS.CN]: {
    CURRENCY_CODE: 'CNY',
    PRECISION: 0,
    locale: LANGUAGE_CODES.ZH_CN,
  },
  [POS.FR]: {
    CURRENCY_CODE: 'EUR',
    PRECISION: 2,
    locale: LANGUAGE_CODES.FR_FR,
  },
  [POS.DE]: {
    CURRENCY_CODE: 'EUR',
    PRECISION: 2,
    locale: LANGUAGE_CODES.DE_DE,
  },
  [POS.HK]: {
    CURRENCY_CODE: 'HKD',
    PRECISION: 2,
    locale: LANGUAGE_CODES.EN_HK,
  },
  [POS.IE]: {
    CURRENCY_CODE: 'EUR',
    PRECISION: 2,
    locale: LANGUAGE_CODES.EN_IE,
  },
  [POS.IT]: {
    CURRENCY_CODE: 'EUR',
    PRECISION: 2,
    locale: LANGUAGE_CODES.EN_IT,
  },
  [POS.JP]: {
    CURRENCY_CODE: 'JPY',
    PRECISION: 0,
    locale: LANGUAGE_CODES.JA_JP,
  },
  [POS.NL]: {
    CURRENCY_CODE: 'EUR',
    PRECISION: 2,
    locale: 'nl-NL',
  },
  [POS.PL]: {
    CURRENCY_CODE: 'PLN',
    PRECISION: 2,
    locale: 'pl-PL',
  },
  [POS.ES]: {
    CURRENCY_CODE: 'EUR',
    PRECISION: 2,
    locale: 'es-ES',
  },
  [POS.CH]: {
    CURRENCY_CODE: 'CHF',
    PRECISION: 2,
    locale: LANGUAGE_CODES.DE_CH,
  },
  [POS.GB]: {
    CURRENCY_CODE: 'GBP',
    PRECISION: 2,
    locale: LANGUAGE_CODES.EN_GB,
  },
  [POS.US]: {
    CURRENCY_CODE: 'USD',
    PRECISION: 2,
    locale: LANGUAGE_CODES.EN_US,
  },
  [POS.LU]: {
    CURRENCY_CODE: 'EUR',
    PRECISION: 2,
    locale: LANGUAGE_CODES.EN_LU,
  },
  [POS.KR]: {
    CURRENCY_CODE: 'KRW',
    PRECISION: 0,
    locale: LANGUAGE_CODES.KO_KR,
  },
};

export const LocalCurrencyFormat: Record<string, { symbol: string; position: 'prefix' | 'suffix' }> = {
  NOK: { symbol: ',-', position: 'suffix' },
  SEK: { symbol: ':-', position: 'suffix' },
  DKK: { symbol: ',-', position: 'suffix' },
  EUR: { symbol: '€', position: 'suffix' },
  HKD: { symbol: 'HK$', position: 'prefix' },
  JPY: { symbol: '¥', position: 'prefix' },
  PLN: { symbol: 'zł', position: 'prefix' },
  CHF: { symbol: 'CHF', position: 'prefix' },
  GBP: { symbol: '£', position: 'prefix' },
  USD: { symbol: '$', position: 'prefix' },
  CAD: { symbol: '$', position: 'prefix' },
  THB: { symbol: '฿', position: 'prefix' },
  CNY: { symbol: '¥', position: 'prefix' },
  AUD: { symbol: 'A$', position: 'prefix' },
  ISK: { symbol: 'kr', position: 'suffix' },
  SGD: { symbol: 'S$', position: 'prefix' },
  KRW: { symbol: '₩', position: 'prefix' },
};

export const languageCodeMap: { [k in keyof typeof POS]: string } = {
  [POS.BE]: 'nl', // Dutch (Note: Belgium is multilingual, but Dutch is most widely spoken)
  [POS.CN]: 'zh', // Chinese
  [POS.DK]: 'da', // Danish
  [POS.FI]: 'fi', // Finnish
  [POS.FR]: 'fr', // French
  [POS.DE]: 'de', // German
  [POS.HK]: 'zh', // Chinese (Cantonese)
  [POS.IE]: 'en', // English
  [POS.IT]: 'it', // Italian
  [POS.JP]: 'ja', // Japanese
  [POS.NL]: 'nl', // Dutch
  [POS.NO]: 'no', // Norwegian
  [POS.PL]: 'pl', // Polish
  [POS.ES]: 'es', // Spanish
  [POS.SE]: 'sv', // Swedish
  [POS.CH]: 'de', // German (Note: Switzerland is multilingual; German is most widely spoken)
  [POS.GB]: 'en', // English
  [POS.US]: 'en', // English
  [POS.LU]: 'en', // English
  [POS.CA]: 'en', // English (Note: Canada is bilingual, but English is most commonly used)
  [POS.KR]: 'ko', // Korean
} as const;

export enum HttpStatusCode {
  Ok = 200,
  Accepted = 202,
  BadRequest = 400,
  Unauthorized = 401,
  Forbidden = 403,
  NotFound = 404,
  TooManyRequests = 429,
  InternalServerError = 500,
}

export type FlightOffersMode = ValueOf<typeof FlightOffersMode>;
export const FlightOffersMode = {
  OWC: 'OWC',
  OWD: 'OWD',
  DAPI: 'DAPI',
} as const;

export const FLIGHT_OFFERS_MODE_MAP = {
  [FlightOffersMode.OWC]: 'combinable',
  [FlightOffersMode.OWD]: 'display',
  [FlightOffersMode.DAPI]: 'dapi',
} as const;

export type CabinOrderTypes = ValueOf<typeof CabinOrderTypes>;
export const CabinOrderTypes = { GO: 'GO', ECONOMY: 'ECONOMY', PREMIUM: 'PREMIUM', PLUS: 'PLUS', BUSINESS: 'BUSINESS' };
export const CabinTypeOrderArray = [
  CabinOrderTypes.GO,
  CabinOrderTypes.PLUS,
  CabinOrderTypes.ECONOMY,
  CabinOrderTypes.PREMIUM,
  CabinOrderTypes.BUSINESS,
];

export type ShortRulesBaggage = ValueOf<typeof ShortRulesBaggage>;
export const ShortRulesBaggage = {
  BagUnderSeat: 'under-seat',
  Bag1x8: '1x8',
  Bag1x23: '1x23',
  Bag2x23: '2x23',
  Bag2x8: '2x8',
  Bag2x3: '2x32',
} as const;

export const ShortRulesBaggageArray = Object.values(ShortRulesBaggage);

export type ShortRulesRefundAndRebook = ValueOf<typeof ShortRulesRefundAndRebook>;
export const ShortRulesRefundAndRebook = {
  NoRebook: 'no-rebook',
  Rebook: 'rebook',
  RebookFee: 'rebook-fee',
  RebookFareDifference: 'rebook-fare-difference',
  NoRefund: 'no-refund',
  Refund: 'refund',
  RefundFee: 'refund-fee',
  FreeCancel: 'free cancel',
} as const;

export const ShortRulesRefundAndRebookArray = Object.values(ShortRulesRefundAndRebook);

export type MaintenanceModeKey = ValueOf<typeof MaintenanceModeKey>;
export const MaintenanceModeKey = {
  Rebooking: 'rebooking-in-maintenance-mode',
  Revenue: 'revenue-in-maintenance-mode',
  Award: 'award-in-maintenance-mode',
  PartnerAward: 'partner-award-in-maintenance-mode',
  TravelPass: 'travel-pass-in-maintenance-mode',
  SelfReac: 'self-reaccommodation-in-maintenance-mode',
} as const;

export type TravelerExtraCardContentId = ValueOf<typeof TravelerExtraCardContentId>;
export const TravelerExtraCardContentId = {
  MEAL: 'card-anc-travel-extra-meal',
  LOUNGE: 'card-anc-travel-extra-lounge',
  FAST_TRACK: 'card-anc-travel-extra-fast-track',
  BIOFUEL: 'card-anc-travel-extra-biofuel',
} as const;

export type PayableOrderState = ValueOf<typeof PayableOrderState>;
export const PayableOrderState = {
  BOOKING_CONFIRMED: 'BOOKING_CONFIRMED',
  BOOKING_FAILED: 'BOOKING_FAILED',
  PAYABLE: 'PAYABLE',
  PAYMENT_LOCKED: 'PAYMENT_LOCKED',
} as const;

export const SupportedFrequentTravelerPrograms = [
  'SK',
  'AR',
  'AM',
  'UX',
  'AF',
  'CI',
  'MU',
  'DL',
  'EY',
  'GA',
  'KQ',
  'KL',
  'KE',
  'SV',
  'SQ',
  'RO',
  'TP',
  'VN',
  'VS',
  'MF',
];
